/**
 * LazySection — Custom Element v4
 */
class LazySection extends HTMLElement {
  static fetchedIds = new Set();
  static inFlight = new Set();
  static queue = [];
  static _timer = null;
  static DEBOUNCE_MS = 100;
  static BATCH_SIZE = 5;

  constructor() {
    super();
    this._resolveId();
  }

  _resolveId() {
    const wrapper =
      this.closest('[id^="shopify-section-"]') ||
      this.closest('.shopify-section');

    if (wrapper && wrapper.id) {
      this.sectionId = wrapper.id.replace('shopify-section-', '');
    } else {
      this.sectionId = null;
    }
  }

  connectedCallback() {
    if (!this.sectionId) return;

    if (LazySection.fetchedIds.has(this.sectionId)) return;

    const trigger = this.dataset.triggerEvent || 'intersection-observer';

    if (trigger === 'mouseover') {
      if (window.innerWidth < 990) return;
      const target = document.querySelector(this.dataset.triggerTarget || 'body');
      if (target) target.addEventListener('mouseover', () => this._enqueue(), { once: true });
    } else if (trigger === 'click') {
      const target = document.querySelector(this.dataset.triggerTarget || 'body');
      if (target) target.addEventListener('click', () => this._enqueue(), { once: true });
    } else {
      const io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(this);
          this._enqueue();
        },
        { rootMargin: '200px 0px 200px 0px' }
      );
      io.observe(this);
    }
  }

  _enqueue() {
    if (LazySection.fetchedIds.has(this.sectionId)) return;
    if (LazySection.inFlight.has(this.sectionId)) return;
    if (LazySection.queue.includes(this.sectionId)) return;

    LazySection.queue.push(this.sectionId);

    clearTimeout(LazySection._timer);
    LazySection._timer = setTimeout(() => LazySection._flush(), LazySection.DEBOUNCE_MS);
  }

  static _flush() {
    const pending = LazySection.queue.splice(0);

    for (let i = 0; i < pending.length; i += LazySection.BATCH_SIZE) {
      const batch = pending.slice(i, i + LazySection.BATCH_SIZE);

      batch.forEach((id) => LazySection.inFlight.add(id));
      LazySection._fetchBatch(batch);
    }
  }

  static _fetchBatch(ids) {
    const params = new URLSearchParams(window.location.search);
    params.set('sections', ids.join(','));
    params.set('section_rendering', 'true');
    const url = `${window.location.pathname}?${params.toString()}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        LazySection._inject(json, ids);
      })
      .catch((err) => {
        ids.forEach((id) => {
          LazySection.inFlight.delete(id);
          LazySection._hideShimmer(id);
        });
      });
  }

  static _inject(json, requestedIds) {
    for (const [sectionId, html] of Object.entries(json)) {
      if (!html || !html.trim()) {
        LazySection.inFlight.delete(sectionId);
        continue;
      }

      const wrapper = document.getElementById('shopify-section-' + sectionId);
      if (!wrapper) {
        LazySection.inFlight.delete(sectionId);
        continue;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newNode =
        doc.getElementById('shopify-section-' + sectionId) ||
        doc.querySelector(`[id*="${sectionId}"]`) ||
        doc.querySelector('.shopify-section') ||
        doc.body.firstElementChild;

      if (!newNode) {
        LazySection._hideShimmer(sectionId);
        LazySection.inFlight.delete(sectionId);
        continue;
      }

      if (newNode.id && newNode.id.startsWith('shopify-section-')) {
        wrapper.outerHTML = newNode.outerHTML;
      } else {
        wrapper.innerHTML = newNode.outerHTML;
      }

      LazySection.fetchedIds.add(sectionId);
      LazySection.inFlight.delete(sectionId);

      const injected = document.getElementById('shopify-section-' + sectionId);
      if (injected) {
        injected.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((a) =>
            newScript.setAttribute(a.name, a.value)
          );
          newScript.textContent = oldScript.textContent;
          oldScript.replaceWith(newScript);
        });

        document.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: 'shopify-section-' + sectionId },
          })
        );

        injected.dispatchEvent(
          new CustomEvent('shopify:section:load', { bubbles: true })
        );
      }
    }

    if (Array.isArray(requestedIds)) {
      requestedIds.forEach((id) => {
        if (LazySection.inFlight.has(id)) {
          LazySection.inFlight.delete(id);
        }
      });
    }
  }

  static _hideShimmer(sectionId) {
    const wrapper = document.getElementById('shopify-section-' + sectionId);
    if (!wrapper) return;
    const shimmer = wrapper.querySelector('.skeleton-container');
    if (shimmer) shimmer.style.display = 'none';
  }
}

customElements.define('lazy-section', LazySection);